/**
 * Where a driver event happened, as the passenger-terminal log recorded it.
 *
 * The log's `entry.metadata.lat` / `.lon` are free text. `driverNewEvent`
 * spreads the client's body straight into the entry and never normalises it,
 * and the terminal's validator (DriverHasArrived.schema.ts) only asks for a
 * non-empty string — a driver whose phone gave no fix must still be able to
 * tap "arrived". So every client that has ever posted an event invented its
 * own word for "no position": the portal writes `unavailable`
 * (PortalTripEventService's LOCATION_UNAVAILABLE), the log also holds
 * `unknown` from a client that is not in this tree, and a phone that answered
 * with nothing leaves `0` or `''`. None of those are places, and none of them
 * are words an office reader should have to decode.
 *
 * So nothing here trusts the string: a position counts only when both halves
 * parse to a real number inside the real range. Everything else — any token,
 * present or future, and the null island at 0,0 that an unset field decays to
 * — is the same thing, a missing position, and says so in English.
 */
import { toLatLon } from './geoPoint';

/** What a reader sees instead of coordinates. One sentence, no jargon. */
export const NO_POSITION = 'No location recorded';

/** Why, in as much as we can honestly claim to know. Shown as a tooltip. */
export const NO_POSITION_HINT = 'The driver\'s phone did not share a position for this event —'
  + ' location was declined, unavailable, or the app was in the background.';

// Coordinates outside these cannot be a point on Earth, whatever they parse
// to. Kept here rather than in geoPoint's toLatLon so the portal's route map
// keeps behaving exactly as it does today; this log is the one place fed by
// strings a human typed nothing into.
const inRange = ({ lat, lon }) => Math.abs(lat) <= 90 && Math.abs(lon) <= 180;

/**
 * `{ lat, lon }` as numbers, or null when the event carries no real position.
 * `metadata` is one terminal-log entry's metadata object.
 */
export const eventPoint = (metadata) => {
  const point = toLatLon({ latitude: metadata?.lat, longitude: metadata?.lon });
  return point && inRange(point) ? point : null;
};

/** Five decimals is about a metre — more digits are GPS noise, not accuracy. */
const DECIMALS = 5;

/** The coordinates for display, or the plain-English stand-in. */
export const formatPosition = (metadata) => {
  const point = eventPoint(metadata);
  if (!point) return NO_POSITION;
  return `${point.lat.toFixed(DECIMALS)}, ${point.lon.toFixed(DECIMALS)}`;
};

/** An OpenStreetMap page centred on the point, for a closer look elsewhere. */
export const osmUrl = ({ lat, lon }, zoom = 16) => (
  `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=${zoom}/${lat}/${lon}`
);
